import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Field } from "../loans/LoanFormDialog";
import { Repayments } from "../../lib/api";
import { receiptRows, printReceipt } from "../../lib/receipt";
import { formatINR, formatIndianDate } from "../../lib/format";
import { toast } from "sonner";
import { Printer, Undo2, Loader2 } from "lucide-react";

export const ReceiptDialog = ({ repId, names, onOpenChange, onReversed }) => {
  const [r, setR] = useState(null);
  const [rev, setRev] = useState(false);
  const [reason, setReason] = useState("");
  const [by, setBy] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { Repayments.get(repId).then(setR).catch(() => toast.error("Could not load receipt")); }, [repId]);

  const reverse = async () => {
    if (reason.trim().length < 5 || by.trim().length < 2) { toast.error("Enter who is reversing and a reason (min 5 characters)"); return; }
    setBusy(true);
    try {
      const updated = await Repayments.reverse(repId, { reason, reversed_by: by });
      setR(updated); setRev(false); toast.success(`Receipt ${updated.receipt_number} reversed`); onReversed();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto bg-white" data-testid="receipt-dialog">
        {!r ? <div className="p-8 text-center text-sm text-emerald-900/60"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Loading…</div> : <>
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-emerald-950" data-testid="receipt-number">{r.receipt_number}</DialogTitle>
            <DialogDescription>{r.status === "reversed" ? "This receipt has been reversed and no longer counts towards the loan." : "Confirmed repayment"}</DialogDescription>
          </DialogHeader>
          {r.status === "reversed" && (
            <div className="rounded-lg border border-rose-300 bg-rose-50 p-3 text-xs text-rose-800" data-testid="receipt-reversed-note">
              Reversed {formatIndianDate(r.reversed_at)} by <strong>{r.reversed_by}</strong>: {r.reversal_reason}
            </div>
          )}
          {r.override && <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
            Excess override authorised by <strong>{r.authorised_by}</strong>: {r.override_reason}</div>}
          <dl className="divide-y divide-emerald-950/5 text-sm">
            {receiptRows(r, names).map(([k, v]) => (
              <div key={k} className="flex justify-between gap-4 py-1.5"><dt className="text-emerald-900/60">{k}</dt><dd className="text-right font-medium text-emerald-950">{v || "—"}</dd></div>
            ))}
          </dl>
          {r.allocations?.length > 0 && (
            <div className="text-xs text-emerald-900/70">
              <div className="mb-1 font-semibold uppercase tracking-wider">Allocation</div>
              {r.allocations.map((a) => <div key={a.instalment_no}>Instalment #{a.instalment_no}: interest {formatINR(a.interest)} · principal {formatINR(a.principal)}</div>)}
            </div>
          )}
          {r.audit?.length > 0 && (
            <div className="text-xs text-emerald-900/60" data-testid="receipt-audit">
              <div className="mb-1 font-semibold uppercase tracking-wider">Audit trail</div>
              {r.audit.map((a) => <div key={a.id}>{formatIndianDate(a.at)} · {a.action.replace("repayment.", "")} · {a.by}</div>)}
            </div>
          )}
          {rev && (
            <div className="grid grid-cols-1 gap-3 rounded-xl border border-rose-300 bg-rose-50 p-3" data-testid="receipt-reverse-form">
              <Field label="Reversed by" required><Input value={by} onChange={(e) => setBy(e.target.value)} data-testid="receipt-reverse-by" /></Field>
              <Field label="Reason" required><Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="receipt-reverse-reason" /></Field>
              <p className="text-xs text-rose-800">The receipt is kept on record and marked reversed. The loan balance is recalculated.</p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setRev(false)}>Cancel</Button>
                <Button size="sm" disabled={busy} onClick={reverse} className="bg-rose-700 text-white hover:bg-rose-800" data-testid="receipt-reverse-confirm">
                  {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}Confirm reversal</Button>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            {r.status === "confirmed" && !rev && (
              <Button variant="outline" onClick={() => setRev(true)} className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="receipt-reverse-btn">
                <Undo2 className="mr-1.5 h-4 w-4" />Reverse</Button>
            )}
            <Button onClick={() => !printReceipt(r, names) && toast.error("Allow pop-ups to print receipts")} className="bg-[#0F4C3A] text-white hover:bg-emerald-900" data-testid="receipt-print-btn">
              <Printer className="mr-1.5 h-4 w-4" />Print receipt</Button>
          </DialogFooter>
        </>}
      </DialogContent>
    </Dialog>
  );
};
